import { FILTROS, ORDENES, type Filtro, type Orden as OrdenDeCartera } from './cartera';
import { unaDe, type Almacen } from './preferencias';
import { columnasALaVista } from './tablero-clientes';

/**
 * CÓMO VE «CLIENTES» CADA USUARIO (tanda 6): la preferencia `cartera`, que lo acompaña de la
 * laptop al teléfono (`usePreferencia`). PURO. Lo guardado puede venir de otra versión o estar
 * roto: todo pasa por `sanearPrefCartera` antes de usarse.
 *
 * Aquí viven también «Mis vistas»: la pantalla armada a su gusto y guardada con un nombre. Cada
 * operación recibe la preferencia y devuelve la nueva; si no hay nada que hacer (un nombre que no
 * vale, una vista que ya no existe), devuelve LA MISMA, y así quien llama sabe que no pasó nada.
 */

export type { OrdenDeCartera };

export interface PrefCartera {
  /** `fuentes` (T14): no es una forma de ver la lista, es la salud de cada base de clientes. */
  vista: 'tabla' | 'kanban' | 'tarjetas' | 'lista' | 'fuentes';
  agrupar: 'etapa' | 'temperatura' | 'tipo' | 'asignado' | 'fuente' | 'nada';
  orden: OrdenDeCartera; // los de lib/cartera.ts
  columnas: string[]; // ids visibles, en orden
  densidad: 'comoda' | 'compacta';
  /** Cuál de los filtros de lib/cartera.ts está puesto (`todos` = ninguno). */
  filtro: Filtro;
  /** Solo los de esta etapa (`''` = todas). La búsqueda no se guarda. */
  etapa: string;
  /** T14: solo los de estas fuentes (`[]` = todas). */
  fuentes: string[];
  guardadas: VistaGuardada[];
  deEntrada: string | null; // id de la vista guardada con que abre
  /** Id de la vista guardada que está puesta, aunque después se le haya cambiado algo. */
  aplicada: string | null;
}

/** Lo que una vista guardada recuerda de la pantalla. */
export type ConfigDeVista = Omit<PrefCartera, 'guardadas' | 'deEntrada' | 'aplicada'>;

export interface VistaGuardada {
  id: string;
  nombre: string;
  config: ConfigDeVista;
}

export const PREF_CARTERA_DE_FABRICA: PrefCartera = {
  vista: 'tarjetas',
  agrupar: 'etapa',
  orden: 'nombre',
  // Vacío: las columnas que la tabla trae encendidas de fábrica.
  columnas: [],
  densidad: 'comoda',
  filtro: 'todos',
  etapa: '',
  fuentes: [],
  guardadas: [],
  deEntrada: null,
  aplicada: null,
};

const VISTAS = ['tabla', 'kanban', 'tarjetas', 'lista', 'fuentes'] as const;
const AGRUPAR = ['etapa', 'temperatura', 'tipo', 'asignado', 'fuente', 'nada'] as const;
const DENSIDADES = ['comoda', 'compacta'] as const;
/** Cuántas vistas se pueden guardar, y de qué largo puede ser el nombre de cada una. */
export const TOPE_DE_VISTAS = 20;
export const LARGO_DEL_NOMBRE = 40;
// Tope para que una preferencia rota no pese: con los de arriba cabe de sobra en los 32 KB del servidor.
const TOPE_COLUMNAS = 60;
// Las fuentes son nombres libres (hasta 60 caracteres, como en el servidor); no se filtra por más de 30 a la vez.
const TOPE_FUENTES = 30;

const esObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const texto = (v: unknown, tope: number): string => (typeof v === 'string' ? v.slice(0, tope) : '');

function sanearConfig(crudo: Record<string, unknown>): ConfigDeVista {
  const f = PREF_CARTERA_DE_FABRICA;
  return {
    vista: unaDe(VISTAS, f.vista)(crudo.vista),
    agrupar: unaDe(AGRUPAR, f.agrupar)(crudo.agrupar),
    orden: unaDe(ORDENES.map((o) => o.id), f.orden)(crudo.orden),
    columnas: Array.isArray(crudo.columnas)
      ? [...new Set(crudo.columnas.filter((c): c is string => typeof c === 'string' && c !== ''))].slice(0, TOPE_COLUMNAS)
      : [],
    densidad: unaDe(DENSIDADES, f.densidad)(crudo.densidad),
    // Una preferencia de antes de «Mis vistas» no los trae: sin filtro y todas las etapas.
    filtro: unaDe(FILTROS.map((x) => x.id), f.filtro)(crudo.filtro),
    // Las etapas son del negocio: aquí no se sabe cuáles hay. La pantalla ignora la que ya no exista.
    etapa: texto(crudo.etapa, 64),
    // Igual que la etapa: una fuente que ya no tiene nadie solo deja de encontrar a alguien.
    fuentes: Array.isArray(crudo.fuentes)
      ? [...new Set(crudo.fuentes.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.slice(0, 60)))].slice(0, TOPE_FUENTES)
      : [],
  };
}

/** Lo guardado, ya de fiar: basura → la de fábrica; cada parte que no valga vuelve a la suya. */
export function sanearPrefCartera(crudo: unknown): PrefCartera {
  if (!esObjeto(crudo)) return PREF_CARTERA_DE_FABRICA;
  const guardadas: VistaGuardada[] = [];
  for (const g of Array.isArray(crudo.guardadas) ? crudo.guardadas : []) {
    // Sin id o sin nombre no se puede elegir ni borrar; un id repetido tampoco.
    if (!esObjeto(g) || !esObjeto(g.config) || !texto(g.id, 64) || !texto(g.nombre, LARGO_DEL_NOMBRE).trim()) continue;
    if (guardadas.some((x) => x.id === g.id) || guardadas.length >= TOPE_DE_VISTAS) continue;
    guardadas.push({ id: texto(g.id, 64), nombre: texto(g.nombre, LARGO_DEL_NOMBRE).trim(), config: sanearConfig(g.config) });
  }
  const existe = (id: unknown): id is string => guardadas.some((g) => g.id === id);
  return {
    ...sanearConfig(crudo),
    guardadas,
    deEntrada: existe(crudo.deEntrada) ? crudo.deEntrada : null,
    aplicada: existe(crudo.aplicada) ? crudo.aplicada : null,
  };
}

/** Donde la pantalla guardaba «lista» o «tablero» antes de la tanda 6, solo en ese navegador. */
export const CLAVE_VIEJA_DE_VISTA = 'conversia.cartera.vista';

/**
 * Con qué arranca quien todavía no eligió nada: la de fábrica, con la tabla si la pantalla es de
 * escritorio (en el teléfono, las tarjetas) y respetando la vista que había elegido con la
 * pantalla vieja («tablero» es el kanban de hoy).
 */
export function prefDeArranque(almacen: Almacen | null, escritorio = false): PrefCartera {
  let vieja: string | null = null;
  try {
    vieja = almacen?.getItem(CLAVE_VIEJA_DE_VISTA) ?? null;
  } catch {
    /* sin permiso para leer: la de fábrica */
  }
  const vista = vieja === 'tablero' ? 'kanban' : vieja === 'lista' ? 'lista' : escritorio ? 'tabla' : null;
  return vista ? { ...PREF_CARTERA_DE_FABRICA, vista } : PREF_CARTERA_DE_FABRICA;
}

// ---------- «Mis vistas» ----------

/** Cómo está armada la pantalla ahora mismo: lo que se guarda en una vista. */
export function configDe(pref: PrefCartera): ConfigDeVista {
  const { vista, agrupar, orden, columnas, densidad, filtro, etapa, fuentes } = pref;
  return { vista, agrupar, orden, columnas, densidad, filtro, etapa, fuentes };
}

/** El nombre de una vista ya limpio (sin espacios de sobra), o null si no vale: vacío o de más de 40 caracteres. */
export function nombreDeVista(crudo: string): string | null {
  const nombre = crudo.trim().replace(/\s+/g, ' ');
  return nombre.length >= 1 && nombre.length <= LARGO_DEL_NOMBRE ? nombre : null;
}

/** La vista guardada que ya se llama así, sin distinguir mayúsculas ni acentos. */
export function vistaLlamada(pref: PrefCartera, nombre: string): VistaGuardada | undefined {
  const buscado = nombreDeVista(nombre);
  if (!buscado) return undefined;
  return pref.guardadas.find((g) => g.nombre.localeCompare(buscado, 'es', { sensitivity: 'base' }) === 0);
}

/** ¿Cabe una vista más? (Reemplazar una que ya existe siempre se puede.) */
export function cabeOtraVista(pref: PrefCartera): boolean {
  return pref.guardadas.length < TOPE_DE_VISTAS;
}

/**
 * Guarda la pantalla de ahora con ese nombre y la deja puesta. Si ya hay una vista con ese
 * nombre, la reemplaza (preguntarlo antes es de quien llama): así se guardan también los cambios
 * de la vista que está puesta. Con un nombre que no vale, o con el tope lleno, no hace nada.
 */
export function guardarVista(pref: PrefCartera, nombre: string, generarId: () => string): PrefCartera {
  const limpio = nombreDeVista(nombre);
  if (!limpio) return pref;
  const config = configDe(pref);
  const ya = vistaLlamada(pref, limpio);
  if (ya) return { ...pref, guardadas: pref.guardadas.map((g) => (g.id === ya.id ? { ...g, nombre: limpio, config } : g)), aplicada: ya.id };
  if (!cabeOtraVista(pref)) return pref;
  const id = generarId();
  // Un id vacío o repetido no se podría elegir ni borrar (y el saneo lo tiraría al volver a leer).
  if (!id || pref.guardadas.some((g) => g.id === id)) return pref;
  return { ...pref, guardadas: [...pref.guardadas, { id, nombre: limpio, config }], aplicada: id };
}

/** Pone entera una vista guardada: vista, agrupación, orden, columnas, densidad, filtro, etapa y fuentes. */
export function aplicarVista(pref: PrefCartera, id: string): PrefCartera {
  const guardada = pref.guardadas.find((g) => g.id === id);
  return guardada ? { ...pref, ...guardada.config, aplicada: guardada.id } : pref;
}

/** Le cambia el nombre. No hace nada si el nombre no vale o si ya lo lleva OTRA vista. */
export function renombrarVista(pref: PrefCartera, id: string, nombre: string): PrefCartera {
  const limpio = nombreDeVista(nombre);
  if (!limpio || !pref.guardadas.some((g) => g.id === id)) return pref;
  const otra = vistaLlamada(pref, limpio);
  if (otra && otra.id !== id) return pref;
  return { ...pref, guardadas: pref.guardadas.map((g) => (g.id === id ? { ...g, nombre: limpio } : g)) };
}

/** La borra. Si era la de entrada, la pantalla vuelve a abrir como la dejaron; la pantalla de ahora no cambia. */
export function borrarVista(pref: PrefCartera, id: string): PrefCartera {
  if (!pref.guardadas.some((g) => g.id === id)) return pref;
  return {
    ...pref,
    guardadas: pref.guardadas.filter((g) => g.id !== id),
    deEntrada: pref.deEntrada === id ? null : pref.deEntrada,
    aplicada: pref.aplicada === id ? null : pref.aplicada,
  };
}

/** «Abrir con esta»: la marca como la de entrada (solo una lo es). Con `null` se quita la marca. */
export function marcarDeEntrada(pref: PrefCartera, id: string | null): PrefCartera {
  if (id !== null && !pref.guardadas.some((g) => g.id === id)) return pref;
  return pref.deEntrada === id ? pref : { ...pref, deEntrada: id };
}

function mismaConfig(a: ConfigDeVista, b: ConfigDeVista): boolean {
  return (
    a.vista === b.vista &&
    a.agrupar === b.agrupar &&
    a.orden === b.orden &&
    a.densidad === b.densidad &&
    a.filtro === b.filtro &&
    a.etapa === b.etapa &&
    [...a.fuentes].sort().join('\n') === [...b.fuentes].sort().join('\n') &&
    // Sin columnas guardadas son «las de entrada»: volver a ellas a mano no es haber cambiado nada.
    columnasALaVista(a.columnas).join() === columnasALaVista(b.columnas).join()
  );
}

/** La vista guardada que está puesta y si la pantalla ya no es como se guardó; null si no hay ninguna puesta. */
export function vistaAplicada(pref: PrefCartera): { id: string; modificada: boolean } | null {
  const guardada = pref.guardadas.find((g) => g.id === pref.aplicada);
  return guardada ? { id: guardada.id, modificada: !mismaConfig(configDe(pref), guardada.config) } : null;
}

/** Con qué se abre la pantalla: la vista «de entrada», si hay una marcada y existe; si no, como la dejó. */
export function alAbrir(pref: PrefCartera): PrefCartera {
  return pref.deEntrada ? aplicarVista(pref, pref.deEntrada) : pref;
}
